import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Package, Calendar, DollarSign, Hash, FileText, Building2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useManualAssets } from '@/providers/manual/hooks/useManualAssets';
import { formatCurrency } from '@/lib/portfolioCalculations';
import { ASSET_CLASS_ICONS } from '@/constants/investmentIcons';
import { PageContainer } from '@/components/ui/PageContainer';

export default function AssetDetail() {
  const { assetId } = useParams<{ assetId: string }>();
  const navigate = useNavigate();
  const { assets, isLoading } = useManualAssets();

  // Find the specific asset
  const asset = assets.find(a => a.id === assetId);

  if (isLoading) {
    return (
      <PageContainer maxWidthClassName="max-w-[960px]">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">Loading asset details...</p>
          </div>
        </div>
      </PageContainer>
    );
  }

  if (!asset) {
    return (
      <PageContainer maxWidthClassName="max-w-[960px]">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Asset Not Found</h2>
            <p className="text-muted-foreground mb-4">The asset you're looking for doesn't exist or has been removed.</p>
            <Button onClick={() => navigate('/assets')} variant="outline">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Assets
            </Button>
          </div>
        </div>
      </PageContainer>
    );
  }

  // Get the appropriate icon for the asset type
  const AssetIcon = ASSET_CLASS_ICONS[asset.asset_type] || ASSET_CLASS_ICONS['other'];

  return (
    <PageContainer maxWidthClassName="max-w-[960px]">
      <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => navigate('/assets')}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Assets
          </Button>
          <div className="flex items-center gap-3">
            <div className="bg-slate-100 rounded-full p-3">
              <AssetIcon className="h-6 w-6 text-[#8B5CF6]" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">{asset.asset_name}</h1>
              <div className="flex items-center gap-2 mt-1">
                <Badge variant="secondary">
                  {asset.asset_type.charAt(0).toUpperCase() + asset.asset_type.slice(1)}
                </Badge>
                <Badge variant="outline">
                  {asset.registered === 'registered' ? 'Registered' : 'Non-Registered'}
                </Badge>
              </div>
            </div>
          </div>
        </div>
        <Button onClick={() => navigate('/assets', { state: { openAssetId: asset.id } })}>
          Edit Asset
        </Button>
      </div>

      {/* Asset Details */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Market Value */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Market Value</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrency(asset.market_value, asset.currency, { showDecimals: true, locale: 'en-CA' })}
            </div>
          </CardContent>
        </Card>

        {/* Book Value */}
        {asset.book_value && (
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Book Value</CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {formatCurrency(asset.book_value, asset.currency, { showDecimals: true, locale: 'en-CA' })}
              </div>
              {asset.market_value !== asset.book_value && (
                <p className="text-xs text-muted-foreground mt-1">
                  {asset.market_value > asset.book_value ? 'Gain' : 'Loss'}: {' '}
                  {formatCurrency(Math.abs(asset.market_value - asset.book_value), asset.currency, { showDecimals: true, locale: 'en-CA' })}
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Quantity */}
        {asset.quantity && (
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Quantity</CardTitle>
              <Hash className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{asset.quantity}</div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Additional Information */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Asset Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              Asset Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {asset.symbol && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Symbol/Identifier</label>
                <p className="text-sm">{asset.symbol}</p>
              </div>
            )}
            
            {asset.type && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Type Classification</label>
                <p className="text-sm">{asset.type}</p>
              </div>
            )}

            <div>
              <label className="text-sm font-medium text-muted-foreground">Currency</label>
              <p className="text-sm">{asset.currency}</p>
            </div>

            <div>
              <label className="text-sm font-medium text-muted-foreground">Added On</label>
              <p className="text-sm flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                {new Date(asset.created_at).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </p>
            </div>

            {asset.updated_at && asset.updated_at !== asset.created_at && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Last Updated</label>
                <p className="text-sm flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  {new Date(asset.updated_at).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Description */}
        {asset.description && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Description
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                {asset.description}
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Dialog deprecated; use Assets drawer via deep link */}
      </div>
    </PageContainer>
  );
}
